import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";

// Featured slug pinned to today's flagship, same product used by the buy
// card below the hero. Swap this constant (or promote to an admin-set
// field) when a different SKU becomes the featured flagship.
const FEATURED_SLUG = "retatrutide-pen";

// Two-column hero: left side keeps the headline + CTA copy that used to
// dominate the whole strip; right side (desktop-only) shows the
// Retatrutide 40mg Pen Kit box render, the same asset shipped on the
// hero buy card, so the two panels visually connect. Mobile keeps the
// compact single-column layout.
export default function Hero() {
  const { format: fmtPrice } = useCurrency();
  // Live price pill, pulls the featured product straight from the API
  // (same cached query the HeroBuyCard uses, so this is free after the
  // first fetch). Price falls back to a subtle "From —" while the request
  // is in-flight so we don't ship a stale hardcoded number on first paint.
  const { data } = useQuery({
    queryKey: ["product", FEATURED_SLUG],
    queryFn: async () => (await api.get(`/products/${FEATURED_SLUG}`)).data,
  });
  const featuredProduct = data?.product;

  return (
    <section className="relative overflow-hidden border-b border-line" data-testid="hero">
      <div className="container-pd pt-10 pb-6 lg:pt-14 lg:pb-8">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <p className="label-mono text-brandblue">↳ Research peptides · UK &amp; EU dispatch</p>
            <h1 className="mt-3 font-heading text-4xl font-black leading-[1.02] tracking-tight text-ink sm:text-5xl lg:text-6xl">
              Research peptides,
              <br />
              <span className="accent-italic">without the guesswork.</span>
            </h1>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-subtle">
              UK and EU-dispatched research peptides. Third-party tested on every lot, COA published before purchase.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/peptides"
                className="btn-pill bg-brandblue px-7 py-3.5 text-sm font-bold text-white hover:bg-brandbluedark"
                data-testid="hero-shop-cta"
              >
                Shop All Products →
              </Link>
              <Link
                to="/verify"
                className="btn-pill btn-outline px-7 py-3.5 text-sm font-semibold"
                data-testid="hero-verify-cta"
              >
                Verify a Box →
              </Link>
            </div>
          </div>

          {/* Right column on desktop; stacks below the copy block on
              mobile. The composed image is smaller on mobile, max-w-[340px]
             , so it doesn't dominate above-the-fold. Drift animation and
              badge stay on all viewports; they're CSS-only and cheap. */}
          <div className="lg:col-span-5" data-testid="hero-product-wrap">
            <Link
              to={`/product/${FEATURED_SLUG}`}
              className="group relative block"
              data-testid="hero-product-link"
            >
              <img
                src="/product-photos/retatrutide-40mg-pen/hero-composition.webp"
                alt="Retatrutide 40mg Pen Kit, box and pen composition"
                className="hero-comp-float mx-auto block h-auto w-full max-w-[340px] object-contain transition-transform duration-500 group-hover:-translate-y-1 lg:max-w-[560px]"
                // Homepage LCP hero: fetch as high-priority so the
                // browser races it against the main JS bundle rather
                // than the lazy card thumbnails behind it.
                fetchpriority="high"
                decoding="async"
                width="560"
                height="560"
                draggable={false}
                data-testid="hero-product-image"
              />
              <span
                className="hero-badge-fadein absolute right-2 top-2 z-10 inline-flex items-center gap-1.5 rounded-full border border-brandblue/20 bg-white/90 px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-brandblue shadow-[0_10px_30px_-12px_rgba(30,58,138,0.35)] backdrop-blur sm:right-6 sm:top-6 sm:gap-2 sm:px-4 sm:py-2 sm:text-[11px] sm:tracking-[0.14em]"
                data-testid="hero-product-badge"
              >
                40mg Pen Kit
                <span className="text-subtle" aria-hidden="true">·</span>
                <span data-testid="hero-product-price">
                  From {featuredProduct ? fmtPrice(featuredProduct.price, { fractionDigits: 2 }) : "—"}
                </span>
                <span className="text-subtle" aria-hidden="true">·</span>
                <span className="text-brandbluedark">Best Seller</span>
              </span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
